import * as React from "react";
import { useState, useEffect } from "react";
import FormControl from "@mui/material/FormControl";
import Typography from "@mui/material/Typography";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import PageLoading from "../../components/PageLoading";
import { kronosTimecard } from "../../api/kronos";
import { selectTechOpsUserSession } from "../../slices/techOpsUserSession/techOpsUserSession";
import { useSelector } from "react-redux";
import { makeStyles } from "@mui/styles";
import { Divider } from "@mui/material";
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import {
  DataGridPro,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  useGridApiRef,
} from "@mui/x-data-grid-pro";

const useStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.down("md")]: {
      marginTop: "5px",
      paddingLeft: "10px",
      paddingRight: "10px",
      marginRight: "0px",
      marginLeft: "0px",
    },
    overflow: "hidden",
    height: "92%",
    fontSize: "18px",
    marginTop: "5px",
    boxSizing: "border-box",
    paddingLeft: "20px",
    paddingRight: "20px",
    marginRight: "0px",
    marginLeft: "0px",
  },
  paySelect: {
    [theme.breakpoints.down("md")]: {
      paddingLeft: "10px",
      paddingRight: "10px",
    },
    fontSize: "16px",
    paddingLeft: "30px",
    paddingRight: "30px",
  },
  eID: {
    [theme.breakpoints.down("md")]: {
      paddingLeft: "10px",
      paddingRight: "10px",
    },
    paddingLeft: "30px",
    paddingRight: "30px",
    },
  buttonSubmit: {
    [theme.breakpoints.down("md")]: {
      width: "95vw",
      float: "none",
      marginRight: "0px",
      marginLeft: "0px",
    },
    height: "40px",
    fontSize: "0.8rem",
    float: "right",
    marginRight: ".9vw",
    marginBottom: "10px",
  },
  buttonContainer: {
    [theme.breakpoints.down("md")]: {
      justifyContent: "center",
      marginRight: "0px",
      marginLeft: "0px",
    },
    justifyContent: "right",
    marginRight: "30px",
    marginLeft: "0px",
  }
}))

function CustomDetailPanelToggle(props) {
  const { paycodes: codes, expand: isExpanded } = props;
  const hasDetail = () => {
    if (codes.length === 0) { return true }
    return false
  }

  return (
    <IconButton
      size="medium"
      tabIndex={-1}
      disabled={hasDetail()}
      aria-label={isExpanded ? 'Close' : 'Open'}
    >
      <ExpandMoreIcon
        sx={{
          transform: `rotateZ(${isExpanded ? 180 : 0}deg)`,
          transition: (theme) =>
            theme.transitions.create('transform', {
              duration: theme.transitions.duration.shortest,
            }),
        }}
        fontSize="inherit"
      />
    </IconButton>
  );
}

function DetailPanelContent({ row: rowProp }) {

  return (
    <Grid sx={{ flex: 1, mx: "auto", width: "90%", paddingTop: 2 }}>
      <DataGridPro
        autoHeight={true}
        density="compact"
        columns={[
          { field: "PayCodeName", headerName: "Pay Codes", flex: 1 },
          { field: "AmountInTime", headerName: "Hours", flex: 1,
          renderCell: (params) => {
            let hours = params.row.AmountInTime
            return hours.toFixed(1)
          }},
        ]}
        rows={rowProp.totals}
        sx={{ flex: 1, mx: "auto", width: "90%", p: 1 }}
        hideFooter
        />
    </Grid>
  )
}

export default function TimecardForm() {
  const payPeriodList = ["Current Pay Period", "Previous Pay Period"]
  const [isLoading, setIsLoading] = useState(false)
  const [apiTimecardPaycodes, setApiTimecardPaycodes] = useState(null)
  const [selectPayPeriod, setSelectPayPeriod] = useState("Current Pay Period")
  const [selectedTablePayPeriod, setSelectedTablePayPeriod] = useState("Current Pay Period")
  const [apiError, setApiError] = useState("")
  const techOpsUserSession = useSelector(selectTechOpsUserSession);
  const eID = (techOpsUserSession.eid).slice(1).padStart(6, "0");
  const classes = useStyles();
  const apiRef = useGridApiRef();

  useEffect(() => {
    setIsLoading(true)
    kronosTimecard(eID, selectPayPeriod)
    .then((res) => res.json())
    .then(async (result) => {
      if (!result.error && !result.message) {
        setApiTimecardPaycodes(result.DateTotals)
        setIsLoading(false)
      }
      setApiError(result.message)
      setIsLoading(false)
    })
    .catch((event) => {
      setApiError(event.message)
      console.error("Error after API Response", event);
      setApiTimecardPaycodes(null)
      setIsLoading(false)
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eID])

  const getTotalhours = () => {
    let totalHours = 0
    apiTimecardPaycodes.forEach((paycode) => {
      totalHours += paycode.grandTotal
    })
    return totalHours
  }

  const columns = [
    { field: "date", headerName: "Date", flex: 1 },
    { field: "grandTotal", headerName: `Hours: ${apiTimecardPaycodes ? getTotalhours() : 0}`, flex: 1, 
    renderCell: (params) => {
      let grandtotal = params.row.grandTotal
      return grandtotal.toFixed(1);
    }},
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      renderCell: (params) => (
        <CustomDetailPanelToggle id={params.id} paycodes={params.row.totals} expand={params.value} />
      ),
    },
  ];

  const handleSubmit = () => {
    setIsLoading(true)
    setApiTimecardPaycodes(null)
    setApiError("")
    setSelectedTablePayPeriod(selectPayPeriod)
    kronosTimecard(eID, selectPayPeriod)
    .then((res) => res.json())
    .then(async (result) => {
      if (!result.error && !result.message) {
        setApiTimecardPaycodes(result.DateTotals)
        setIsLoading(false)
      }
      setApiError(result.message)
      setIsLoading(false)
    })
    .catch((event) => {
      setApiError(event.message)
      console.error("Error after API Response", event.message);
      setApiTimecardPaycodes(null)
      setIsLoading(false)
    });
  };

  const onRowClick = (params) => {
    if(params.row.totals.length !== 0) {
      apiRef.current.toggleDetailPanel(params.id);
    }
  };

  const payPeriodFormSelect = (event) => {
    setSelectPayPeriod(event.target.value);
  }

  const listPayPeriodFormOptions = (list) => {
    return (list).map((period) => {
      return (<MenuItem key={period} value={period}>{period}</MenuItem>);
    })
  }

  const eidFormField = () => {
    return (
      <Grid item xs={12} sm={12} md={6}>
        <Grid container justifyContent="center">
          <Grid item xs={12} className={classes.eID}>
            <FormControl fullWidth margin="normal">
              <TextField
                disabled
                name="eID"
                data-testid="eID-text-field"
                label="Employee ID"
                variant="outlined"
                defaultValue={techOpsUserSession.eid}
              />
            </FormControl>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  const periodFormField = () => {
    return (
      <Grid item xs={12} sm={12} md={6}>
        <Grid container justifyContent="center">
          <Grid item xs={12} className={classes.paySelect}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Pay Period</InputLabel>
              <Select
                data-testid="period-select"
                name="payPeriod"
                onChange={payPeriodFormSelect}
                defaultValue="Current Pay Period"
                label="Integration"
              >
                {listPayPeriodFormOptions(payPeriodList)}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  const submitButton = () => {
    return (
      <Grid item className={classes.buttonContainer} xs={12}>
        <Button
          className={classes.buttonSubmit}
          data-testid="submit-button"
          id="submit-button"
          variant="contained"
          type="submit"
          onClick={handleSubmit}
          disabled={isLoading}
        >
          Submit
        </Button>
      </Grid>
    )
  }

  const timeGridData = () => {
    return (
      <>
        {
          isLoading ?
          <Grid container justifyContent="center">
            <PageLoading />
          </Grid>
          :
          <>
            {
              apiTimecardPaycodes ?
              <Grid
                container
                spacing={1}
                direction="column"
                className={classes.root}
              >
                <Typography sx={{fontSize: "20px", padding: "10px"}} align="center">
                  {`Summary for ${selectedTablePayPeriod}`}
                </Typography>
                <Typography sx={{fontSize: "14px"}} align="left">
                  <NotificationImportantIcon fontSize="inherit" />
                  {` There will be a 1-2 day delay for updated information.
                  For current and up to date information, please see a Supervisor.`}
                </Typography>
                <DataGridPro
                  apiRef={apiRef}
                  className={classes.mainTable}
                  rows={apiTimecardPaycodes}
                  columns={columns}
                  hideFooter
                  onRowClick={onRowClick}
                  getDetailPanelHeight={() => "auto"}
                  getDetailPanelContent={({ row }) => !row.AmountInTime ? <DetailPanelContent row={row} /> : null}
                />
              </Grid>
              :
              <Typography>{apiError}</Typography>
            }
          </>
        }
      </>
    )
  }

  return (
    <Stack
      className={classes.root}
      direction="column"
    >
      <Grid
        container
        spacing={1}
        direction="row"
        alignItems="center"
      >
        {eidFormField()}
        {periodFormField()}
        {submitButton()}
      </Grid>
      <Divider />
      {timeGridData()}
    </Stack>
  );
}
